import * as Sentry from "@sentry/react";
import {useQuery} from "@tanstack/react-query";
import toast from "react-hot-toast";

import {retriggerInitializationForStores} from "api/admin/devices/devices";
import type {ApiError} from "types/api";

interface RetriggerInitializationResponse {
  success: boolean;
}

interface RetriggerInitializationQueryResponse {
  data: {
    success: boolean;
  } | null;
  isFetching: boolean;
  retriggerInitialization: () => void;
}

export function useRetriggerInitializationForStores({
  storeIds,
}: {
  storeIds: number[];
}): RetriggerInitializationQueryResponse {
  const {
    data,
    isFetching,
    refetch: retriggerInitialization,
  } = useQuery<unknown, ApiError, RetriggerInitializationResponse>({
    queryKey: ["retrigger-device-initialization", storeIds],
    queryFn: async () => {
      if (!storeIds.length) {
        return null;
      }

      const {
        data: {success},
      } = await retriggerInitializationForStores({
        storeIds,
      });

      toast(
        'Successfully triggered initialization for devices',
        {
          duration: 4000,
        },
      );

      return {
        success,
      };
    },
    enabled: false,
    onError: (error) => {
      Sentry.captureException(error);
      toast.error(
        error?.response?.data?.error ??
          "An error occurred while triggering initialization for devices",
        {
          duration: 4000,
        }
      );
    },
  });

  return {
    data: data as RetriggerInitializationQueryResponse["data"],
    isFetching,
    retriggerInitialization,
  };
}
